import {SortingDropdown, type OrderOptions} from '@github-ui/dashboard-sorting-dropdown/SortingDropdown'
import type {Action} from '@github-ui/action-bar'
import {ListView} from '@github-ui/list-view'
import {ListViewDensityToggle} from '@github-ui/list-view/ListViewDensityToggle'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'

import styles from '../shared/SharedListContainer.module.css'

import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'
import {GlobalCommands, ScopedCommands} from '@github-ui/ui-commands'
import {useAnalytics} from '@github-ui/use-analytics'
import {
  DEFAULT_SORTING_KEY,
  defaultSortingOptions,
  issuesDashboardSortingOptions,
} from '../shared/consts/sorting-consts'
import {
  type ComponentProps,
  type ElementType,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useMemo,
  useRef,
} from 'react'

import type {DashboardIssueIdentifier, EnrichedIssueListItem} from './types/list-types'
import type {EnrichedPullListItem} from '../pulls/types/list-types'
import {IssueListItem} from './IssueListItem'
import {PullsListItem} from '../pulls/PullsListItem'
import {activateFocusedRow} from './helpers/activate-focused-row'
import {getListItemIdentity} from '../shared/helpers/item-identity'
import {SharedDashboardBlankslate} from '../shared/SharedDashboardBlankslate'
import {SharedListLoadingItems} from '../shared/SharedListLoadingSkeleton'
import type {EmptyListState} from '../shared/types/blankslate-types'
import {
  DisplayMode,
  type FilterSelectType,
  type PageInfo,
  type StateCountPageInfo,
  type StateFilter,
} from '../shared/types/list-types'

// The issues dashboard renders a true discriminated union: PR rows require an
// explicit `itemType: 'pull_request'` (the backend always sets it). A type guard
// keeps the dispatch sound and lets the else-branch narrow cleanly to
// `EnrichedIssue`; anything without the PR discriminator uses the issue row,
// which reads a strict subset of fields and degrades more gracefully.
type MixedListRow = EnrichedIssueListItem | (EnrichedPullListItem & {itemType: 'pull_request'})

function isPullRequestItem(item: MixedListRow): item is EnrichedPullListItem & {itemType: 'pull_request'} {
  return item.itemType === 'pull_request'
}

// Collision-free row key. See `getListItemIdentity`.
function rowKey(item: MixedListRow): string {
  return getListItemIdentity(item)
}

function isModifiedClick(event: MouseEvent) {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- preserve native modified-link navigation
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
}

function hasStateCounts(pageInfo: PageInfo): pageInfo is StateCountPageInfo {
  return 'openCount' in pageInfo && 'closedCount' in pageInfo
}

const noop = () => {}
const defaultIssuesSortingOptions = {...defaultSortingOptions, ...issuesDashboardSortingOptions}

export interface IssuesListSelection {
  selectedIssueIds: ReadonlySet<string>
  selectedCount?: number
  disabled?: boolean
  selectAllCheckboxRef?: RefObject<HTMLInputElement | null>
  onIssueSelect: (issueId: string, isSelected: boolean, isRangeSelection?: boolean) => void
  onToggleSelectAll: (isSelected: boolean) => void
  selectionAction?: ReactNode
  actions?: ComponentProps<typeof ListViewMetadata>['actions']
}

// Once focus is inside the list, `ListView`'s own focus zone owns `j`/`k` movement.
// Registering scoped no-ops stops the global handlers from re-focusing the roving
// tab stop, and swallows the key before it reaches the document-level listener.
const NOOP_FOCUS_COMMANDS = {
  'shared-dashboard-lists:focus-next-issue': noop,
  'shared-dashboard-lists:focus-previous-issue': noop,
} as const
const NO_SCOPED_COMMANDS = {}

interface IssuesListProps {
  issues: MixedListRow[]
  pageInfo: PageInfo | StateCountPageInfo
  stateCounts?: {open: number | undefined; closed: number | undefined}
  compactMode: boolean
  onCompactModeChange: (value: boolean) => void
  onSortingItemSelect: (key: string) => void
  onStateFilterSelect: (state: StateFilter) => void
  emptyListState: EmptyListState
  defaultSortingKey?: string
  sortingItemSelected?: string
  sortingOptions?: Record<string, OrderOptions>
  issueMetadataContainerClassName?: string
  separateAgentAssignees?: boolean
  showRepository?: boolean
  stateItemSelected?: StateFilter
  showStateFilterCounts?: boolean
  onFilterSelect?: (type: FilterSelectType, value: string) => void
  onIssueFieldSelect?: (fieldName: string, queryValue: string) => void
  isLoading?: boolean
  isDeferredLoading?: boolean
  isAgentAttributionEnabled?: boolean
  showDensityToggle?: boolean
  filterActions?: Action[]
  /**
   * When true, the list stops handling `j`/`k` entirely — it registers neither the global
   * focus commands nor the scoped no-ops — yielding those keys to whatever else currently owns
   * them. On the issues dashboard that is the open issue side panel, which binds the same keys to
   * step between issues. `o` is unaffected: it only fires when focus is already inside the list.
   *
   * Suppressing registration (rather than registering no-op handlers) is deliberate. Global
   * commands share one page-wide registry, and `getKeybindingMap` collapses duplicate keybindings
   * to the last one registered — so two live `j` commands make ownership depend on mount order,
   * which flips whenever the list remounts under an open panel. Not registering removes the
   * ambiguity instead of racing for it.
   */
  focusShortcutsSuppressed?: boolean
  /**
   * When provided, clicking an issue row opens it in a side panel instead of navigating.
   * Only issue rows honor this; PR rows always navigate. The clicked anchor is passed
   * back so the caller can restore focus to it when the panel closes.
   */
  onIssueSelect?: (identifier: DashboardIssueIdentifier, returnFocusElement: HTMLElement) => void
  /** Builds a query-preserving URL that filters the list by an issue type name. */
  getIssueTypeHref: (issueTypeName: string) => string
  /** Optional client-router link component for issue type tokens (SPA navigation). */
  issueTypeLinkComponent?: ElementType
  /** Builds the query-preserving URL to filter the list to blocked issues. */
  getIssueBlockedHref?: () => string
  /** Builds the query-preserving URL to filter the list by a parent issue. */
  getParentIssueHref?: (repoNameWithOwner: string, number: number) => string
  /** Builds the query-preserving URL for an issue state filter. */
  getStateFilterHref?: (state: StateFilter) => string
  selection?: IssuesListSelection
  emptyState?: ReactNode
}

const EMPTY_SELECTED_ISSUE_IDS = new Set<string>()

export function IssuesList({
  issues,
  pageInfo,
  compactMode,
  onCompactModeChange,
  defaultSortingKey = DEFAULT_SORTING_KEY,
  sortingItemSelected,
  sortingOptions = defaultIssuesSortingOptions,
  issueMetadataContainerClassName,
  separateAgentAssignees = false,
  showRepository = true,
  onSortingItemSelect,
  stateItemSelected,
  showStateFilterCounts = true,
  stateCounts,
  onStateFilterSelect,
  emptyListState,
  onFilterSelect,
  onIssueFieldSelect,
  isLoading = false,
  isDeferredLoading,
  isAgentAttributionEnabled,
  showDensityToggle = true,
  filterActions,
  focusShortcutsSuppressed,
  onIssueSelect,
  getIssueTypeHref,
  getIssueBlockedHref,
  getParentIssueHref,
  getStateFilterHref,
  issueTypeLinkComponent,
  selection,
  emptyState,
}: IssuesListProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  const resultText = pageInfo.totalCount === 1 ? 'result' : 'results'
  const pageInfoStateCounts = hasStateCounts(pageInfo)
    ? {open: pageInfo.openCount, closed: pageInfo.closedCount}
    : undefined
  const stateFilterCounts = stateCounts ?? pageInfoStateCounts
  const shouldShowStateFilters =
    (stateItemSelected === 'open' || stateItemSelected === 'closed') &&
    (!showStateFilterCounts || stateFilterCounts?.open !== undefined || stateFilterCounts?.closed !== undefined)
  const selectedIssueIds = selection?.selectedIssueIds ?? EMPTY_SELECTED_ISSUE_IDS
  const selectedCount = selection?.selectedCount ?? selectedIssueIds.size
  const anyIssuesSelected = selectedCount > 0

  const listRef = useRef<HTMLElement | undefined>(undefined)

  const focusTabbableItem = useCallback(() => {
    const tabbableItem = listRef.current?.querySelector('[tabindex="0"]')
    if (tabbableItem instanceof HTMLElement) {
      tabbableItem.focus()
    }
  }, [])

  const activateFocusedItem = useCallback(() => activateFocusedRow(listRef.current), [])

  const globalFocusCommands = useMemo(
    () => ({
      'shared-dashboard-lists:focus-next-issue': focusTabbableItem,
      'shared-dashboard-lists:focus-previous-issue': focusTabbableItem,
    }),
    [focusTabbableItem],
  )

  const scopedListCommands = useMemo(
    () => ({
      // The no-ops only exist to stop the *global* focus handlers from re-focusing the roving tab
      // stop. With those unregistered there is nothing to suppress, and keeping them would swallow
      // `j`/`k` before they reach the owner that is now handling them.
      ...(focusShortcutsSuppressed ? undefined : NOOP_FOCUS_COMMANDS),
      'shared-dashboard-lists:open-focused-issue': activateFocusedItem,
    }),
    [activateFocusedItem, focusShortcutsSuppressed],
  )

  const listView = (
    <ListView
      title="Issues"
      titleHeaderTag="h2"
      className={styles.listContainer}
      listRef={listRef}
      onVariantChange={value => onCompactModeChange(value === DisplayMode.Compact)}
      variant={compactMode ? DisplayMode.Compact : DisplayMode.Default}
      aria-busy={isLoading}
      isSelectable={Boolean(selection)}
      isSelectionDisabled={selection?.disabled}
      selectedCount={selectedCount}
      totalCount={pageInfo.totalCount}
      singularUnits="issue"
      pluralUnits="issues"
      emptyState={
        emptyState ?? (
          <SharedDashboardBlankslate dashboardType="issues" variant="empty" emptyListState={emptyListState} />
        )
      }
      metadata={
        <ListViewMetadata
          onToggleSelectAll={selection?.onToggleSelectAll}
          selectAllCheckboxRef={selection?.selectAllCheckboxRef}
          selectionAction={selection?.selectionAction}
          title={
            anyIssuesSelected || shouldShowStateFilters ? undefined : (
              <span data-testid="results-count">
                {pageInfo.totalCount} {resultText}
              </span>
            )
          }
          sectionFilters={
            !anyIssuesSelected && shouldShowStateFilters ? (
              <>
                <ListViewSectionFilterLink
                  key="open"
                  title="Open"
                  href={getStateFilterHref?.('open') ?? '#'}
                  onClick={e => {
                    if (e.defaultPrevented) return
                    if (getStateFilterHref && isModifiedClick(e)) return
                    e.preventDefault()
                    sendAnalyticsEvent('issues-dashboard.click', 'STATE_FILTER', {state: 'open'})
                    onStateFilterSelect('open')
                  }}
                  isSelected={stateItemSelected === 'open'}
                  count={showStateFilterCounts ? stateFilterCounts?.open?.toLocaleString('en-US') : undefined}
                />
                <ListViewSectionFilterLink
                  key="closed"
                  title="Closed"
                  href={getStateFilterHref?.('closed') ?? '#'}
                  onClick={e => {
                    if (e.defaultPrevented) return
                    if (getStateFilterHref && isModifiedClick(e)) return
                    e.preventDefault()
                    sendAnalyticsEvent('issues-dashboard.click', 'STATE_FILTER', {state: 'closed'})
                    onStateFilterSelect('closed')
                  }}
                  isSelected={stateItemSelected === 'closed'}
                  count={showStateFilterCounts ? stateFilterCounts?.closed?.toLocaleString('en-US') : undefined}
                />
              </>
            ) : undefined
          }
          densityToggle={showDensityToggle ? <ListViewDensityToggle /> : undefined}
          actionsLabel={anyIssuesSelected ? 'Issue actions' : 'Issue filters'}
          actions={anyIssuesSelected ? selection?.actions : (filterActions ?? [])}
        >
          {!anyIssuesSelected ? (
            <div className="mr-2">
              <SortingDropdown
                sortingItemSelected={sortingItemSelected || ''}
                onSortingItemSelect={onSortingItemSelect}
                defaultSortingKey={defaultSortingKey}
                sortingOptions={sortingOptions}
                directionRestrictedKeys={['relevance']}
                sortFieldsRequireDescSuffix
              />
            </div>
          ) : null}
        </ListViewMetadata>
      }
    >
      {isLoading ? (
        <SharedListLoadingItems dashboardType="issues" numItems={5} announce />
      ) : issues.length > 0 ? (
        issues.map(item =>
          isPullRequestItem(item) ? (
            <PullsListItem
              key={rowKey(item)}
              pull={item}
              onFilterSelect={onFilterSelect}
              isDeferredLoading={isDeferredLoading}
              isAgentAttributionEnabled={isAgentAttributionEnabled}
              sortingItemSelected={sortingItemSelected}
            />
          ) : (
            <IssueListItem
              key={rowKey(item)}
              issue={item}
              isSelected={selectedIssueIds.has(rowKey(item))}
              onSelect={(isSelected, isRangeSelection) =>
                selection?.onIssueSelect(rowKey(item), isSelected, isRangeSelection)
              }
              metadataContainerClassName={issueMetadataContainerClassName}
              separateAgentAssignees={separateAgentAssignees}
              showRepository={showRepository}
              onFilterSelect={onFilterSelect}
              onIssueFieldSelect={onIssueFieldSelect}
              onIssueSelect={onIssueSelect}
              getIssueTypeHref={getIssueTypeHref}
              getIssueBlockedHref={getIssueBlockedHref}
              getParentIssueHref={getParentIssueHref}
              issueTypeLinkComponent={issueTypeLinkComponent}
              sortingItemSelected={sortingItemSelected}
            />
          ),
        )
      ) : null}
    </ListView>
  )

  return (
    <>
      {!isLoading && issues.length > 0 && !focusShortcutsSuppressed ? (
        <GlobalCommands commands={globalFocusCommands} />
      ) : null}
      <ScopedCommands commands={isLoading || issues.length === 0 ? NO_SCOPED_COMMANDS : scopedListCommands}>
        {listView}
      </ScopedCommands>
    </>
  )
}
